import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box, Typography, Card, CardContent, TextField, Button, Avatar, CircularProgress,
} from '@mui/material';
import { PersonAdd as PersonAddIcon, UploadFile as ImportIcon } from '@mui/icons-material';

import PatientImportDialog from '../components/PatientImportDialog';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Provider-facing patient registration. Only reachable when a clinic admin has
 * enabled "providers can add patients" (the backend also enforces this). Posts to
 * the clinical create-patient endpoint, which assigns the new patient to the
 * creating provider.
 */
export default function AddPatient() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', date_of_birth: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        date_of_birth: form.date_of_birth || null,
        reason: form.reason.trim() || null,
      };
      const res = await axios.post(`${API_URL}/api/v1/clinical/patients`, payload);
      toast.success(res.data?.status === 'linked'
        ? 'Patient linked — they were notified in their portal'
        : 'Patient registered — an invite was sent to set up their account');
      navigate('/patients');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to register patient');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Avatar sx={{ bgcolor: 'secondary.main', width: 48, height: 48 }}><PersonAddIcon /></Avatar>
        <Box>
          <Typography variant="h4" fontWeight={700}>Add Patient</Typography>
          <Typography variant="body2" color="text.secondary">
            Register a patient and assign them to yourself
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<ImportIcon />} onClick={() => setImportOpen(true)}
          sx={{ ml: 'auto', flexShrink: 0 }}>
          Import patients
        </Button>
      </Box>

      <PatientImportDialog api="clinical" open={importOpen} onClose={() => setImportOpen(false)} />

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth label="Full Name" margin="normal" required
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            />
            <TextField
              fullWidth label="Email" type="email" margin="normal" required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              helperText="The patient receives an invite to set up their own password"
            />
            <TextField
              fullWidth label="Phone" margin="normal"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
            <TextField
              fullWidth label="Date of Birth" type="date" margin="normal"
              InputLabelProps={{ shrink: true }}
              value={form.date_of_birth}
              onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
            />
            <TextField
              fullWidth label="Reason / Chief Complaint" margin="normal" multiline minRows={2}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
            <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button onClick={() => navigate('/patients')} disabled={submitting}>Cancel</Button>
              <Button
                type="submit" variant="contained" disabled={submitting}
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {submitting ? 'Registering…' : 'Register Patient'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
